import React, { useState } from "react"

import { useIntl } from "react-intl"

import useAutosavePopUp from "../../hooks/useAutosavePopUp"
import titleIcon from "../assets/titleIcon.svg"
import RecursiveCategoryTree from "./RecursiveCategoryTree/RecursiveCategoryTree"
import ColorableSVG from "~/components/ColorableSVG/ColorableSVG"
import ErrorMessage from "~/components/ErrorMessage/ErrorMessage"
import Separator from "~/components/Separator/Separator"
import { Body, Button, Heading } from "~/components/ui"
import useCategoriesArray from "~/hooks/useCategoriesArray"
import { Category } from "~/models/Category"
import { useUpdateRecipeCategoriesMutation } from "~/state/api/communityRecipe/communityRecipeCategories"

// eslint-disable-next-line css-modules/no-unused-class
import css from "./CategoryStep.module.scss"

interface PropTypes {
  onSuccess: () => void
  recipeUuid: string | undefined
}

export interface CategoryWithChildren extends Category {
  children: CategoryWithChildren[]
}

const getChilds = (
  Categories: Category[],
  parentId: string
): CategoryWithChildren[] => {
  return Categories.filter(category => category.parentId === parentId).map(
    cat => {
      return {
        ...cat,
        children: getChilds(Categories, cat.categoryId),
      }
    }
  )
}

const getCategoryTree = (Categories: Category[]) => {
  let roots: CategoryWithChildren[] = [],
    i: number

  for (i = 0; i < Categories.length; i += 1) {
    if (!Categories[i].parentId) {
      roots.push({
        ...Categories[i],
        children: getChilds(Categories, Categories[i].categoryId),
      })
    }
  }
  return roots
}

const CategoryStep = ({ onSuccess, recipeUuid, ...rest }: PropTypes) => {
  const intl = useIntl()
  const CATEGORIES = useCategoriesArray(intl.locale) as Category[]
  const categoryTree = getCategoryTree(CATEGORIES)
  const [selectedCategoryId, setSelectedCategoryId] = useState<null | string>(
    null
  )

  const [displayError, setDisplayError] = useState<boolean>(false)
  const [
    updateRecipeCategories,
    {
      isLoading: updateIsLoading,
      isError: updateIsError,
      error: updateError,
      isSuccess: updateIsSuccess,
    },
  ] = useUpdateRecipeCategoriesMutation()

  useAutosavePopUp({
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading,
    isError: updateIsError,
    error: updateError,
    onSuccess: onSuccess,
  })

  const handleClick = async () => {
    if (selectedCategoryId) {
      recipeUuid &&
        (await updateRecipeCategories({
          uuid: recipeUuid,
          body: [{ uuid: selectedCategoryId }],
        }))
    } else {
      setDisplayError(true)
    }
  }
  return (
    <div className={css.root}>
      <div className={css.titleIcon}>
        <ColorableSVG
          href={titleIcon}
          mainColor="var(--gray4-color)"
          height={89}
          width={89}
        />
      </div>
      <Heading variant="extraLarge" className={css.title}>
        {intl.formatMessage({
          id: "recipe-creation/text:category-modal-heading",
        })}
      </Heading>
      <Body variant="body1" className={css.description}>
        {intl.formatMessage({
          id: "recipe-creation/text:category-modal-description",
        })}
      </Body>
      <div className={css.categoryTree}>
        <RecursiveCategoryTree
          categories={categoryTree}
          setSelectedCategoryId={setSelectedCategoryId}
          selectedCategoryId={selectedCategoryId}
          level={0}
        />
      </div>
      <Separator noMargin />
      <Button
        onClick={() => handleClick()}
        color={selectedCategoryId ? "original" : "gray4"}
        fullWidth
        className={css.validate}
      >
        {intl.formatMessage({
          id: "form/action:submit",
        })}
      </Button>
      {displayError && !selectedCategoryId && (
        <ErrorMessage
          error={intl.formatMessage({
            id: "recipe-creation/error:category-modal-select-category",
          })}
        />
      )}
    </div>
  )
}

export default CategoryStep
